import { createContext, useContext, useEffect, useState } from 'react';
import type {
  Dispatch,
  ReactChild,
  ReactFragment,
  ReactPortal,
  SetStateAction
} from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import { useSnackbar } from 'notistack';
import { AccountInfo } from '@azure/msal-browser';
import { useTranslation } from 'react-i18next';

import { AxiosServiceError, UserResponse } from 'interfaces/general';
import { AccessElements, Role } from 'helpers/app.config';
import useGetUserPerms from 'hooks/security/useGetUserPerms';
import useCreateLoginTrack from 'hooks/security/useCreateLoginTrack';
import { TOption } from 'components/shared/ExportOptions';

import { ModalReg } from '../interfaces/components';

export const KEY_ERROR_CONNECTION = 'errorConnection';
export const MESSAGE_ERROR_CONNECTION =
  'common.commonMessages.genericRequestError';

type GlobalContextState = {
  userData?: UserResponse;
  hasAccess: (roles: Role[]) => boolean;
  setUserData?: Dispatch<SetStateAction<UserResponse>>;
  showApiError?: (
    error: AxiosServiceError | string,
    fallbackMessage: string,
    duration?: number
  ) => void;
  showError?: (
    message: string,
    persistAndPreventDuplicate?: boolean,
    key?: string | number,
    duration?: number
  ) => void;
  showApiAlert?: (
    apiError: AxiosServiceError | string,
    message?: string,
    duration?: number
  ) => void;
  showSuccess?: (message: string, duration?: number) => void;
  /** @deprecated */
  currentModal?: ModalReg;
  /** @deprecated */
  modal: (modal: ModalReg, open?: boolean) => void;
  hasPerm: (perm: AccessElements[]) => boolean;
  isLoadingPermissions?: boolean;

  selectedOption?: TOption;
  setSelectedOption?: Dispatch<SetStateAction<TOption>>;
};

const GlobalContext = createContext<GlobalContextState>({
  hasAccess: () => false,
  modal: (): void => {
    console.log('modal');
  },
  hasPerm: () => false
});

function GlobalProvider(props: {
  children:
    | boolean
    | ReactChild
    | ReactFragment
    | ReactPortal
    | null
    | undefined;
}) {
  const [userData, setUserData] = useState<UserResponse | null>();
  const { accounts, instance } = useMsal();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const account: AccountInfo = useAccount(accounts[0]);
  const { i18n, t } = useTranslation('global');

  // Este state lo que hace es revisar que tipo de exportacion se esta haciendo
  const [selectedOption, setSelectedOption] = useState<TOption>(undefined);

  const validateMessageError = (message: string, key?: string | number) => {
    if (!navigator.onLine) {
      return t(MESSAGE_ERROR_CONNECTION);
    }
    if (key) closeSnackbar(key);
    return message;
  };

  const hasAccess = (roles: Role[]): boolean =>
    roles.includes(userData?.extension_GSerRole);

  const { getAccessList } = useGetUserPerms(
    userData?.extension_GSerUserResponsibleId
  );
  const { isLoading: isLoadingPermissions } = getAccessList;

  const hasPerm = (perms: AccessElements[]) => {
    // Si no hay permisos, se asume que tiene acceso
    if (!perms?.length) return true;

    return perms?.some((perm) =>
      getAccessList?.data?.accessList?.includes(perm)
    );
  };

  const { createLoginTracking } = useCreateLoginTrack();

  useEffect(() => {
    if (!account) {
      setUserData(null);
      return;
    }
    const aUserData: UserResponse = account?.idTokenClaims as UserResponse;
    if (aUserData?.isForgotPassword) {
      instance.logout();
      return;
    }
    if (aUserData.extension_GSerAttributes) {
      aUserData.attr = JSON.parse(aUserData.extension_GSerAttributes);
    }
    setUserData(aUserData);
    createLoginTracking({
      userLoggedId: aUserData?.extension_GSerUserId,
      tokenTypeId: 3,
      isLogin: true,
      apkversion: null,
      imei: null
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  const showApiError = (error: AxiosServiceError, fallbackMessage: string) => {
    const msgToShow = validateMessageError(
      error?.response?.data?.message || fallbackMessage
    );

    enqueueSnackbar(msgToShow, {
      variant: 'error',
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right'
      },
      preventDuplicate: true,
      autoHideDuration: 5500
    });
  };

  const showApiAlert = (
    error: AxiosServiceError,
    fallbackMessage: string,
    duration = 5500
  ) => {
    const msgToShow = validateMessageError(
      error?.response?.data?.message || fallbackMessage
    );

    enqueueSnackbar(msgToShow, {
      variant: 'warning',
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right'
      },
      preventDuplicate: true,
      autoHideDuration: duration
    });
  };

  const showError = (
    errorMessage: string,
    persistAndPreventDuplicate = false,
    key?: string | number,
    duration?: number
  ) => {
    const isOffLine = !navigator.onLine;
    const KEY = isOffLine ? KEY_ERROR_CONNECTION : key;
    const msgToShow = validateMessageError(errorMessage, KEY);
    const isPersistAndPreventDuplicate = isOffLine
      ? true
      : persistAndPreventDuplicate;

    enqueueSnackbar(msgToShow, {
      key: KEY,
      variant: 'error',
      preventDuplicate: isPersistAndPreventDuplicate,
      persist: isPersistAndPreventDuplicate,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right'
      },
      autoHideDuration: duration || 5500
    });
  };

  const showSuccess = (successMessage: string, duration?: number) => {
    enqueueSnackbar(successMessage, {
      variant: 'success',
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right'
      },
      preventDuplicate: true,
      autoHideDuration: duration || 5500
    });
  };

  useEffect(() => {
    if (userData) {
      const selectLanguage = async (lang: string) => {
        await i18n.changeLanguage(lang);
      };

      selectLanguage(
        userData.extension_GSerUserLangId === 1 ? 'es' : 'en'
      ).catch(() => {
        showError(t('errorMessages.errorChangingLanguage'));
      });
    }
  }, [userData]);

  /**
   * @deprecated - Los modales ya no se manejan utilizando este estado.
   */
  const [currentModal, setCurrentModal] = useState<ModalReg | 0>(0);

  /**
   * @deprecated - Los modales ya no se manejan utilizando este metodo.
   */
  const modal = (mod: ModalReg, open = true): void => {
    if (open) {
      setCurrentModal(mod);
    } else {
      setCurrentModal(0); // ojo no registrar modales con 0
    }
  };

  return (
    <GlobalContext.Provider
      value={{
        userData,
        hasAccess,
        isLoadingPermissions,
        showApiError,
        showError,
        showApiAlert,
        showSuccess,
        currentModal,
        modal,
        hasPerm,
        selectedOption,
        setSelectedOption
      }}
    >
      {props.children}
    </GlobalContext.Provider>
  );
}

const useGlobalContext = () => useContext(GlobalContext);

export { GlobalContext, GlobalProvider, useGlobalContext };
